import { useTheme, COLOR_THEMES, ColorTheme } from '@/contexts/ThemeContext';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

// Theme color previews - showing the main colors of each theme
const THEME_COLORS: Record<ColorTheme, { bg: string; primary: string; accent: string; text: string }> = {
  casino: { bg: '#141a23', primary: '#f5c542', accent: '#22c55e', text: '#f8fafc' },
  dark: { bg: '#050508', primary: '#f7ca45', accent: '#2ed573', text: '#f2f2f2' },
  light: { bg: '#ffffff', primary: '#b8860b', accent: '#1a8a42', text: '#0a1628' },
};

interface ColorThemeSelectorProps {
  showLabel?: boolean;
  className?: string;
}

export function ColorThemeSelector({ showLabel = true, className = '' }: ColorThemeSelectorProps) {
  const { colorTheme, setColorTheme } = useTheme();

  return (
    <div className={`${className}`}>
      {showLabel && (
        <p className="text-xs text-muted-foreground mb-2">Theme</p>
      )}
      <div className="grid grid-cols-3 gap-2">
        {COLOR_THEMES.map((theme) => {
          const colors = THEME_COLORS[theme.value];
          const isSelected = colorTheme === theme.value;
          
          return (
            <button
              key={theme.value}
              onClick={() => setColorTheme(theme.value)}
              className={cn(
                "relative flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all",
                isSelected 
                  ? "border-primary ring-2 ring-primary/20" 
                  : "border-transparent hover:border-muted-foreground/30"
              )}
              title={theme.description}
            >
              {/* Color swatch preview */}
              <div 
                className="w-full h-8 rounded-md flex items-center justify-center gap-0.5 overflow-hidden"
                style={{ backgroundColor: colors.bg }}
              >
                <div 
                  className="w-2.5 h-5 rounded-sm" 
                  style={{ backgroundColor: colors.primary }}
                />
                <div 
                  className="w-2.5 h-5 rounded-sm" 
                  style={{ backgroundColor: colors.accent }}
                />
                <div 
                  className="w-2 h-3 rounded-sm" 
                  style={{ backgroundColor: colors.text, opacity: 0.7 }}
                />
              </div>
              
              {/* Label */}
              <span className={cn(
                "text-[10px] font-medium",
                isSelected ? "text-primary" : "text-muted-foreground"
              )}>
                {theme.label}
              </span>
              
              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                  <Check className="w-2.5 h-2.5 text-primary-foreground" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Compact inline version for use in settings/profile sections
export function ColorThemeSelectorCompact({ className = '' }: { className?: string }) {
  const { colorTheme, setColorTheme } = useTheme();

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {COLOR_THEMES.map((theme) => {
        const colors = THEME_COLORS[theme.value];
        const isSelected = colorTheme === theme.value;
        
        return (
          <button
            key={theme.value}
            onClick={() => setColorTheme(theme.value)}
            className={cn(
              "relative w-8 h-8 rounded-full border-2 flex items-center justify-center gap-px overflow-hidden transition-all",
              isSelected 
                ? "border-primary ring-2 ring-primary/20 scale-110" 
                : "border-border hover:border-muted-foreground/50"
            )}
            style={{ backgroundColor: colors.bg }}
            title={`${theme.label} - ${theme.description}`}
          >
            <div 
              className="w-1.5 h-3 rounded-sm" 
              style={{ backgroundColor: colors.primary }}
            />
            <div 
              className="w-1.5 h-3 rounded-sm" 
              style={{ backgroundColor: colors.accent }}
            />
            {isSelected && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Mini version for sidebar footers - opens a popover. showLabel renders wider with "{Theme} Theme" text.
export function ColorThemeSelectorMini({ className = '', showLabel = false }: { className?: string; showLabel?: boolean }) {
  const { colorTheme, setColorTheme } = useTheme();
  const currentColors = THEME_COLORS[colorTheme];
  const currentTheme = COLOR_THEMES.find(t => t.value === colorTheme);

  return (
    <Popover>
      <PopoverTrigger asChild>
        {showLabel ? (
          <button
            className={cn(
              "flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all",
              className
            )}
            title={`Theme: ${currentTheme?.label}`}
          >
            {/* Color swatch */}
            <div 
              className="w-7 h-7 rounded-lg border border-white/10 flex items-center justify-center gap-px overflow-hidden flex-shrink-0"
              style={{ backgroundColor: currentColors.bg }}
            >
              <div className="w-2 h-4 rounded-sm" style={{ backgroundColor: currentColors.primary }} />
              <div className="w-2 h-4 rounded-sm" style={{ backgroundColor: currentColors.accent }} />
            </div>
                    <span className="text-sm font-medium text-foreground/80">{currentTheme?.label} Theme</span>
          </button>
        ) : (
          <Button 
            variant="ghost" 
            size="icon"
            className={cn("h-8 w-8", className)}
            title={`Theme: ${currentTheme?.label}`}
          >
            <div 
              className="w-5 h-5 rounded border border-border flex items-center justify-center gap-px overflow-hidden"
              style={{ backgroundColor: currentColors.bg }}
            >
              <div className="w-1.5 h-3 rounded-sm" style={{ backgroundColor: currentColors.primary }} />
              <div className="w-1.5 h-3 rounded-sm" style={{ backgroundColor: currentColors.accent }} />
            </div>
          </Button>
        )}
      </PopoverTrigger>
      <ThemePopoverContent colorTheme={colorTheme} setColorTheme={setColorTheme} />
    </Popover>
  );
}

// Shared popover content for theme selectors
function ThemePopoverContent({ colorTheme, setColorTheme }: { colorTheme: ColorTheme; setColorTheme: (theme: ColorTheme) => void }) {
  return (
    <PopoverContent className="w-56 p-3" align="start" side="top" sideOffset={8} collisionPadding={16}>
      <p className="text-xs font-medium text-muted-foreground mb-3">Select Theme</p>
      <div className="grid grid-cols-3 gap-2">
        {COLOR_THEMES.map((theme) => {
          const colors = THEME_COLORS[theme.value];
          const isSelected = colorTheme === theme.value;
          
          return (
            <button
              key={theme.value}
              onClick={() => setColorTheme(theme.value)}
              className={cn(
                "relative flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all hover:scale-105",
                isSelected 
                  ? "border-primary bg-primary/5" 
                  : "border-transparent hover:border-muted-foreground/30 hover:bg-muted/50"
              )}
              title={theme.description}
            >
              {/* Color swatch preview */}
              <div 
                className="w-full h-8 rounded-md flex items-center justify-center gap-0.5 overflow-hidden shadow-sm"
                style={{ backgroundColor: colors.bg }}
              >
                <div 
                  className="w-2.5 h-5 rounded-sm" 
                  style={{ backgroundColor: colors.primary }}
                />
                <div 
                  className="w-2.5 h-5 rounded-sm" 
                  style={{ backgroundColor: colors.accent }}
                />
              </div>
              
              {/* Label */}
              <span className={cn(
                "text-[10px] font-medium",
                isSelected ? "text-primary" : "text-muted-foreground"
              )}>
                {theme.label}
              </span>
              
              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center shadow-sm">
                  <Check className="w-2.5 h-2.5 text-primary-foreground" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </PopoverContent>
  );
}
